import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { I2gNgTrackingModule } from 'i2g-ng-tracking';
import { PdfJsViewerModule } from 'ng2-pdfjs-viewer';
import { BadgeComponent } from './components/badge/badge.component';
import { ButtonTabsComponent } from './components/button-tabs/button-tabs.component';
import { CardComponent } from './components/card/card.component';
import { ChatFormComponent } from './components/chat-form/chat-form.component';
import { ChatHeaderComponent } from './components/chat-header/chat-header.component';
import { ChatListItemComponent } from './components/chat-list-item/chat-list-item.component';
import { ChatListComponent } from './components/chat-list/chat-list.component';
import { CheckmarkListComponent } from './components/checkmark-list/checkmark-list.component';
import { CheckmarkComponent } from './components/checkmark/checkmark.component';
import { CompanyLogoComponent } from './components/company-logo/company-logo.component';
import { contextMenuComponents } from './components/context-menu/components';
import { DialogManagerComponent } from './components/dialog-manager/dialog-manager.component';
import { DialogBaseComponent } from './components/dialog-templates/dialog-base/dialog-base.component';
import { DialogBasicComponent } from './components/dialog-templates/dialog-basic/dialog-basic.component';
import { DialogLoadingComponent } from './components/dialog-templates/dialog-loading/dialog-loading.component';
import {
  DIALOG_BASIC_COMPONENT,
  DIALOG_LOADING_COMPONENT,
} from './components/dialog-templates/dialog.tokens';
import { DrawerTitleComponent } from './components/drawer-title/drawer-title.component';
import { DrawerComponent } from './components/drawer/drawer.component';
import { DropdownComponent } from './components/dropdown/dropdown.component';
import { EmptyStateComponent } from './components/empty-state/empty-state.component';
import { FilePreviewComponent } from './components/file-preview/file-preview.component';
import { CheckboxFieldComponent } from './components/form/components/checkbox-field/checkbox-field.component';
import { CurrencyFieldComponent } from './components/form/components/currency-field/currency-field.component';
import { ErrorMessageComponent } from './components/form/components/error-message/error-message.component';
import { InputFieldComponent } from './components/form/components/input-field/input-field.component';
import { InputDirective } from './components/form/components/input/input.directive';
import { RadioFieldComponent } from './components/form/components/radio-field/radio-field.component';
import { RatingIconComponent } from './components/form/components/rating-field/components/rating-icon/rating-icon.component';
import { RatingFieldComponent } from './components/form/components/rating-field/rating-field.component';
import { ReadonlyFieldComponent } from './components/form/components/readonly-field/readonly-field.component';
import { SelectFieldComponent } from './components/form/components/select-field/select-field.component';
import { TextAreaFieldComponent } from './components/form/components/text-area-field/text-area-field.component';
import { ToggleFieldComponent } from './components/form/components/toggle-field/toggle-field.component';
import { ToggleComponent } from './components/form/components/toggle/toggle.component';
import { FormMasterComponent } from './components/form/form-master.component';
import { FullScreenSpinnerComponent } from './components/full-screen-spinner/full-screen-spinner.component';
import { HereMapComponent } from './components/here-maps/here-map/here-map.component';
import { HereMarkerComponent } from './components/here-maps/here-marker/here-marker.component';
import { IconComponent } from './components/icon/icon.component';
import { JobAttachmentPreviewComponent } from './components/job-attachment-preview/job-attachment-preview.component';
import { JobDetailHeaderComponent } from './components/job-detail-header/job-detail-header.component';
import { JobDetailListItemComponent } from './components/job-detail-list-item/job-detail-list-item.component';
import { JobDetailListComponent } from './components/job-detail-list/job-detail-list.component';
import { JobEstimatePreviewComponent } from './components/job-estimate-preview/job-estimate-preview.component';
import { JobFileListItemComponent } from './components/job-file-list-item/job-file-list-item.component';
import { JobFileListComponent } from './components/job-file-list/job-file-list.component';
import { JobPreviewTitleComponent } from './components/job-preview-title/job-preview-title.component';
import { LinkComponent } from './components/link/link.component';
import { LoaderComponent } from './components/loader/loader.component';
import { NoteComponent } from './components/note/note.component';
import { NotificationComponent } from './components/notification/notification.component';
import { PoweredByComponent } from './components/powered-by/powered-by.component';
import { ProgressBarComponent } from './components/progress-bar/progress-bar.component';
import { ProgressSpinnerComponent } from './components/progress-spinner/progress-spinner.component';
import { ReviewFormComponent } from './components/review-form/review-form.component';
import { ReviewFormService } from './components/review-form/review-form.service';
import { RouterLinkComponent } from './components/router-link/router-link.component';
import { TableComponent } from './components/table/table.component';
import { ContextMenuComponent } from './components/context-menu/context-menu.component';
import { ImageGalleryComponent } from './components/image-gallery/image-gallery.component';
import { DialogFieldComponent } from './components/form/components/dialog-field/dialog-field.component';
import { NgLetDirective } from './directives/let/let.directive';
import { DatePickerComponent } from './components/form/components/date-picker/date-picker.component';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { ToastMessagesComponent } from './components/toast-messages/toast-messages.component';
import { ToastComponent } from './components/toast/toast.component';
import { TooltipManagerComponent } from './components/tooltip-manager/tooltip-manager.component';
import { TooltipComponent } from './components/tooltip/tooltip.component';
import { PageNotFoundComponent } from './containers/page-not-found/page-not-found.component';
import { AutoCompleteDirective } from './directives/auto-complete/auto-complete.directive';
import { ButtonDirective } from './directives/button/button.directive';
import { ClipboardDirective } from './directives/clipboard/clipboard.directive';
import { DatePickerDirective } from './directives/date-picker/date-picker.directive';
import { DynamicResizeDirective } from './directives/dynamic-resize/dynamic-resize.directive';
import { FocusableDirective } from './directives/focusable/focusable.directive';
import { FormatAsNumberDirective } from './directives/format-as-number/format-as-number.directive';
import { MultiLineTruncateDirective } from './directives/multi-line-truncate/multi-line-truncate.directive';
import { PopperDirective } from './directives/popper/popper.directive';
import { PreventWindowUnloadingDirective } from './directives/prevent-window-unloading/prevent-window-unloading.directive';
import { ScrollIntoFocusDirective } from './directives/scroll-into-focus/scroll-into-focus.directive';
import { StickyDirective } from './directives/sticky/sticky.directive';
import { CapitalizePipe } from './pipes/capitalize/capitalize.pipe';
import { FillPipe } from './pipes/fill/fill.pipe';
import { FriendlyDatePipe } from './pipes/friendly-date/friendly-date.pipe';
import { KebabCasePipe } from './pipes/kebab-case/kebab-case.pipe';
import { LinkProtocolPipe } from './pipes/link-protocol/link-protocol.pipe';
import { MomentDatePipe } from './pipes/moment-date/moment-date.pipe';
import { MoneyPipe } from './pipes/money/money.pipe';
import { PercentagePipe } from './pipes/percentage/percentage.pipe';
import { RemoveCurrencySymbolPipe } from './pipes/remove-currency-symbol/remove-currency-symbol.pipe';
import { SafePipe } from './pipes/safe/safe.pipe';
import { SnakeCasePipe } from './pipes/snake-case/snake-case.pipe';
import { BannerComponent } from './services/banner/banner.component';
import { ChatService } from './services/chat/chat.service';
import { I2gTranslate } from './pipes/translate/translate.pipe';

const dialogComponents: any[] = [
  DialogBaseComponent,
  DialogBasicComponent,
  DialogLoadingComponent,
];

const toastComponents: any[] = [ToastMessagesComponent, ToastComponent];

const materialModules: any[] = [MatDatepickerModule, MatNativeDateModule];

// TODO: [cleanup] Move forms components to another library
const formComponents: any[] = [
  FormMasterComponent,
  CheckboxFieldComponent,
  ErrorMessageComponent,
  RadioFieldComponent,
  InputFieldComponent,
  SelectFieldComponent,
  CurrencyFieldComponent,
  TextAreaFieldComponent,
  ToggleFieldComponent,
  DialogFieldComponent,
  DatePickerComponent,
];

const components: any[] = [
  CheckmarkComponent,
  CheckmarkListComponent,
  ReviewFormComponent,
  CompanyLogoComponent,
  DropdownComponent,
  IconComponent,
  LinkComponent,
  ProgressSpinnerComponent,
  RouterLinkComponent,
  FullScreenSpinnerComponent,
  ButtonTabsComponent,
  BannerComponent,
  NoteComponent,
  NotificationComponent,
  DialogManagerComponent,
  RatingFieldComponent,
  RatingIconComponent,
  PoweredByComponent,
  TooltipComponent,
  TooltipManagerComponent,
  CardComponent,
  HereMapComponent,
  HereMarkerComponent,
  ReadonlyFieldComponent,
  ProgressBarComponent,
  LoaderComponent,
  ToggleComponent,
  DrawerComponent,
  ChatFormComponent,
  ChatListComponent,
  ChatListItemComponent,
  ChatHeaderComponent,
  EmptyStateComponent,
  JobDetailListComponent,
  JobDetailListItemComponent,
  JobDetailHeaderComponent,
  DrawerTitleComponent,
  BadgeComponent,
  FilePreviewComponent,
  JobEstimatePreviewComponent,
  JobFileListComponent,
  JobFileListItemComponent,
  JobAttachmentPreviewComponent,
  JobPreviewTitleComponent,
  TableComponent,
  ContextMenuComponent,
  ImageGalleryComponent,
  ...dialogComponents,
  ...formComponents,
  ...toastComponents,
  ...contextMenuComponents,
];

const containers: any[] = [PageNotFoundComponent];

const directives: any[] = [
  MultiLineTruncateDirective,
  PreventWindowUnloadingDirective,
  FocusableDirective,
  StickyDirective,
  FormatAsNumberDirective,
  DatePickerDirective,
  ButtonDirective,
  AutoCompleteDirective,
  InputDirective,
  ClipboardDirective,
  ScrollIntoFocusDirective,
  DynamicResizeDirective,
  PopperDirective,
  NgLetDirective,
];

const pipes: any[] = [
  CapitalizePipe,
  KebabCasePipe,
  SafePipe,
  SnakeCasePipe,
  FriendlyDatePipe,
  MomentDatePipe,
  FillPipe,
  MoneyPipe,
  PercentagePipe,
  RemoveCurrencySymbolPipe,
  LinkProtocolPipe,
  I2gTranslate,
];

@NgModule({
  imports: [
    CommonModule,
    RouterModule,
    TranslateModule,
    I2gNgTrackingModule.forRoot(),
    ReactiveFormsModule,
    PdfJsViewerModule,
    ...materialModules,
  ],
  providers: [
    {
      provide: DIALOG_BASIC_COMPONENT,
      useValue: DialogBasicComponent,
    },
    {
      provide: DIALOG_LOADING_COMPONENT,
      useValue: DialogLoadingComponent,
    },
    ReviewFormService,
    ChatService,
  ],
  declarations: [
    ...directives,
    ...pipes,
    ...components,
    ...containers,
    ...dialogComponents,
  ],
  entryComponents: [...dialogComponents],
  exports: [
    ...directives,
    ...pipes,
    ...components,
    ...containers,
    ...materialModules,
  ],
})
export class I2gNgUiModule {}
